import React, { useEffect, useMemo, useState } from "react";
import { Paperclip, Send, Upload, X, ArrowUpRight } from "lucide-react";
import CrmV2MailTemplatePickerModal from "./CrmV2MailTemplatePickerModal";

const CrmV2MailComposerModal = ({ open, lead, templates = [], attachments = [], onClose, onSend, onSaveTemplate }) => {
  const [templateOpen, setTemplateOpen] = useState(false);
  const [quickTemplateOpen, setQuickTemplateOpen] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [localAttachments, setLocalAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const selectedTemplate = useMemo(() => templates.find((item) => String(item.id) === String(templateId)), [templates, templateId]);

  useEffect(() => {
    if (!open) return;
    setTemplateOpen(false);
    setQuickTemplateOpen(false);
    setTemplateId("");
    setTo(lead?.email_from || "");
    setCc("");
    setReplyTo("");
    setSubject(lead?.name ? `Re: ${lead.name}` : "");
    setBody("");
    setSelectedAttachments([]);
    setLocalAttachments([]);
    setSending(false);
    setSavingTemplate(false);
  }, [open, lead]);

  if (!open) return null;

  const applyTemplate = (template) => {
    setTemplateId(String(template.id));
    setSubject(template.subject || "");
    setBody(template.body_html || template.body_text || "");
    if (template.attach_documents) setSelectedAttachments(attachments.map((item) => item.id));
    setQuickTemplateOpen(false);
    setTemplateOpen(false);
  };

  const submit = async () => {
    if (!to.trim() || !subject.trim() || !body.trim() || sending) return;
    setSending(true);
    try {
      await onSend?.({ template_id: templateId || undefined, to: to.trim(), cc: cc.trim(), reply_to: replyTo.trim(), subject: subject.trim(), body_html: body, attachment_ids: selectedAttachments, attachment_files: localAttachments });
      onClose();
    } finally {
      setSending(false);
    }
  };

  const addLocalAttachments = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) setLocalAttachments((current) => [...current, ...files]);
    event.target.value = "";
  };

  const removeLocalAttachment = (file) => {
    setLocalAttachments((current) => current.filter((item) => item !== file));
  };

  const saveAsTemplate = async () => {
    if (savingTemplate || !subject.trim() || !body.trim()) return;
    setSavingTemplate(true);
    try {
      const saved = await onSaveTemplate?.({ subject: subject.trim(), body });
      if (saved?.id) setTemplateId(String(saved.id));
    } finally {
      setSavingTemplate(false);
    }
  };

  return (
    <>
      <div style={templateOpen ? styles.hiddenLayer : styles.backdrop} role="dialog" aria-modal="true" aria-label="Send email">
        <div style={{ ...styles.dialog, maxWidth: 920, minHeight: 560 }}>
          <div style={styles.header}>
            <strong>Send email</strong>
            <button type="button" onClick={onClose} style={styles.close} aria-label="Close"><X size={16} /></button>
          </div>
          <div style={styles.body}>
            <Field label="Recipients"><input value={to} onChange={(event) => setTo(event.target.value)} placeholder="Followers of the document and add contacts to notify..." style={styles.input} /></Field>
            <Field label="Cc"><input value={cc} onChange={(event) => setCc(event.target.value)} placeholder="Add carbon copy recipients" style={styles.input} /></Field>
            <Field label="Reply-To"><input value={replyTo} onChange={(event) => setReplyTo(event.target.value)} placeholder="Reply address (optional)" style={styles.input} /></Field>
            <Field label="Subject"><input value={subject} onChange={(event) => setSubject(event.target.value)} style={styles.input} /></Field>
            <Field label="Config Details"><textarea value={body} onChange={(event) => setBody(event.target.value)} rows={9} placeholder="Write your message..." style={styles.textarea} /></Field>
            <div style={styles.attachmentsHeader}><Paperclip size={15} /> ATTACHMENTS</div>
            <div style={styles.attachmentsPanel}>
              <label htmlFor="crm-mail-local-attachment" style={styles.uploadButton}><Upload size={15} /> Add attachment</label>
              <input id="crm-mail-local-attachment" type="file" multiple onChange={addLocalAttachments} style={styles.fileInput} />
              {attachments.map((item) => <label key={item.id} style={styles.attachment}><input type="checkbox" checked={selectedAttachments.includes(item.id)} onChange={() => setSelectedAttachments((current) => current.includes(item.id) ? current.filter((id) => id !== item.id) : [...current, item.id])} /><span>{item.name || item.filename || `Attachment #${item.id}`}</span></label>)}
              {localAttachments.map((file) => <span key={`${file.name}-${file.size}-${file.lastModified}`} style={styles.localAttachment}><Paperclip size={13} /><span>{file.name}</span><button type="button" onClick={() => removeLocalAttachment(file)} style={styles.removeAttachment} aria-label={`Remove ${file.name}`}><X size={13} /></button></span>)}
              {attachments.length === 0 && localAttachments.length === 0 && <span style={styles.noAttachments}>No files attached</span>}
            </div>
            <div style={styles.templateBar}>
              <span>Load template</span>
              {quickTemplateOpen ? (
                <>
                  <select
                    value={templateId}
                    onChange={(event) => {
                      const template = templates.find((item) => String(item.id) === String(event.target.value));
                      if (template) applyTemplate(template);
                    }}
                    style={styles.templateSelect}
                  >
                    <option value="">Select a template...</option>
                    {templates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}
                  </select>
                  <button type="button" onClick={() => setTemplateOpen(true)} style={styles.templateManagerButton}><ArrowUpRight size={15} /></button>
                </>
              ) : (
                <button type="button" onClick={() => setQuickTemplateOpen(true)} style={styles.templateButton}>{selectedTemplate?.name || "Select a template"}</button>
              )}
            </div>
          </div>
          <div style={styles.footer}>
            <button type="button" onClick={submit} disabled={sending || !to.trim() || !subject.trim() || !body.trim()} style={styles.primaryButton}><Send size={13} /> {sending ? "SENDING..." : "SEND"}</button>
            <button type="button" onClick={onClose} style={styles.linkButton}>CANCEL</button>
            <button type="button" onClick={saveAsTemplate} disabled={savingTemplate || !subject.trim() || !body.trim()} style={styles.linkButton}>{savingTemplate ? "SAVING…" : "SAVE AS NEW TEMPLATE"}</button>
          </div>
        </div>
      </div>
      <CrmV2MailTemplatePickerModal open={templateOpen} templates={templates} lead={lead} selectedId={templateId} onClose={() => setTemplateOpen(false)} onApply={applyTemplate} />
    </>
  );
};

const Field = ({ label, children }) => <div style={styles.field}><label>{label}</label><div style={{ minWidth: 0 }}>{children}</div></div>;

const styles = {
  backdrop: { position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "rgba(0,0,0,.48)" },
  hiddenLayer: { visibility: "hidden", pointerEvents: "none" },
  dialog: { width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column", background: "#fff", boxShadow: "0 12px 35px rgba(0,0,0,.26)", fontFamily: '"Lucida Grande", Helvetica, Verdana, Arial, sans-serif', color: "#343a40" },
  header: { minHeight: 48, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 18px", borderBottom: "1px solid #dee2e6", fontSize: 15 },
  close: { border: 0, background: "transparent", color: "#868e96", cursor: "pointer", display: "inline-flex", padding: 6 },
  body: { overflowY: "auto", padding: "18px 22px 8px" },
  field: { display: "grid", gridTemplateColumns: "110px minmax(0, 1fr)", gap: 12, alignItems: "start", minHeight: 42, fontSize: 13, color: "#495057" },
  input: { width: "100%", boxSizing: "border-box", border: 0, borderBottom: "1px solid #ced4da", outline: 0, padding: "7px 2px", fontSize: 14, color: "#495057" },
  textarea: { width: "100%", boxSizing: "border-box", minHeight: 210, border: "1px solid #e9ecef", outline: 0, padding: "12px", resize: "vertical", fontFamily: "inherit", fontSize: 14, lineHeight: 1.6, color: "#343a40" },
  attachmentsHeader: { display: "flex", alignItems: "center", gap: 6, margin: "16px 0 8px 122px", color: "#714b67", fontSize: 12 },
  attachmentsPanel: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, minHeight: 42, margin: "0 0 10px 122px", padding: "10px 12px", border: "1px solid #e9ecef", background: "#fafbfc" },
  uploadButton: { display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 10px", border: "1px solid #adb5bd", background: "#fff", color: "#495057", cursor: "pointer", fontSize: 12 },
  fileInput: { display: "none" },
  attachment: { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 8px", border: "1px solid #dfe3e6", background: "#fff", color: "#495057", fontSize: 12 },
  localAttachment: { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 8px", border: "1px solid #b8d2df", background: "#eef7fb", color: "#315b6d", fontSize: 12 },
  removeAttachment: { display: "inline-flex", alignItems: "center", border: 0, background: "transparent", color: "#714b67", cursor: "pointer", padding: 0 },
  noAttachments: { color: "#868e96", fontSize: 12 },
  templateBar: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, margin: "14px 0 10px 122px", fontSize: 13, color: "#495057" },
  templateButton: { border: 0, borderBottom: "1px solid #ced4da", background: "#fff", color: "#714b67", padding: "5px 2px", cursor: "pointer", textAlign: "left", minWidth: 190 },
  templateSelect: { minWidth: 260, maxWidth: "100%", border: "1px solid #adb5bd", background: "#fff", color: "#343a40", padding: "8px 10px", fontSize: 13, outline: 0 },
  templateManagerButton: { border: "1px solid #adb5bd", background: "#fff", color: "#714b67", padding: "8px 10px", fontSize: 12, cursor: "pointer" },
  footer: { display: "flex", alignItems: "center", gap: 16, minHeight: 58, padding: "0 22px", borderTop: "1px solid #dee2e6" },
  primaryButton: { display: "inline-flex", alignItems: "center", gap: 6, border: 0, background: "#7b9eb2", color: "#fff", padding: "9px 13px", fontSize: 12, cursor: "pointer" },
  linkButton: { border: 0, background: "transparent", color: "#714b67", fontSize: 12, cursor: "pointer", padding: 0 },
};

export default CrmV2MailComposerModal;
